import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  LinearProgress,
} from '@mui/material'
import {
  COLETORES,
  DIAS_MAXIMO_COLETA,
  FORMATO_ID,
  atualizarConta,
  atualizarMarca,
  cadastrarConta,
  coletarAgora,
  criarMarca,
  listarContas,
  listarMarcas,
  removerDemonstracao,
  slugDe,
  ultimasColetas,
  type Coleta,
  situacaoCredenciais,
  ehAdmin,
  contarDadosMarca,
  excluirMarca,
  ROTULOS_DADOS,
  type SituacaoCredenciais,
  type ContaAdmin,
  type MarcaAdmin,
} from '../api/admin'
import { ColetaDiariaCard } from '../components/ColetaDiariaCard'
import { CredenciaisCard } from '../components/CredenciaisCard'
import { nomePlataforma } from '../api/trafegoPago'
import { AtivacaoMarca } from '../components/AtivacaoMarca'
import { EscolherConta, TEM_BUSCA } from '../components/EscolherConta'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'

const PLATAFORMAS_CADASTRO = ['meta_ads', 'google_ads', 'ga4', 'search_console', 'instagram', 'facebook', 'tiktok']

type Dialogo =
  | { tipo: 'nova-marca'; nome: string }
  | { tipo: 'renomear-marca'; nome: string }
  | { tipo: 'ativa-marca'; ativa: boolean }
  | { tipo: 'nova-conta'; plataforma: string; idExterno: string; nome: string }
  | { tipo: 'renomear-conta'; conta: ContaAdmin; nome: string }
  | { tipo: 'coletar'; conta: ContaAdmin; dias: number }
  | { tipo: 'remover-demo' }
  | { tipo: 'excluir-marca'; confirmacao: string; contagem: Record<string, number> | null }
  | null

// Hora da coleta no relógio de São Paulo, qualquer que seja o fuso do navegador.
const quando = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

/** Marcas e as contas de cada uma. Remover é desativar: o histórico fica. */
export function MarcasPage() {
  const { recarregarMarcas } = useFiltros()
  const { pode } = useAuth()
  // Quem só ativa marcas (marcas.conectar) cadastra contas e coleta; renomear, desativar, excluir
  // marca e mexer em credenciais globais é de quem gerencia.
  const gerencia = pode('marcas.gerenciar')
  const [marcas, setMarcas] = useState<MarcaAdmin[] | null>(null)
  const [selecionada, setSelecionada] = useState('')
  const [contas, setContas] = useState<ContaAdmin[] | null>(null)
  const [coletas, setColetas] = useState<Map<string, Coleta>>(new Map())
  const [versao, setVersao] = useState(0)
  const [credenciais, setCredenciais] = useState<SituacaoCredenciais | null>(null)
  const [admin, setAdmin] = useState(false)
  useEffect(() => {
    ehAdmin().then(setAdmin)
  }, [])
  useEffect(() => {
    situacaoCredenciais()
      .then(setCredenciais)
      .catch(() => setCredenciais(null)) // sem o card, a coleta mostra o erro como antes
  }, [])
  /** O que falta para coletar esta plataforma (null = nada, ou ainda não se sabe). */
  const faltaPara = (plataforma: string) => {
    const g = credenciais?.plataformas[plataforma]
    return g && credenciais && !credenciais[g].ok ? credenciais[g].falta : null
  }
  const infoAtivacao = {
    emailContaServico: credenciais?.ativacao?.emailContaServico ?? null,
    mcc: credenciais?.ativacao?.mcc ?? null,
    portfolioMeta: null,
  }
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'warning' | 'error'; texto: string } | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [dialogo, setDialogo] = useState<Dialogo>(null)
  const marca = marcas?.find((m) => m.id === selecionada)

  const carregarMarcas = useCallback(async () => {
    try {
      const lista = await listarMarcas()
      setMarcas(lista)
      setSelecionada((atual) => (lista.some((m) => m.id === atual) ? atual : (lista[0]?.id ?? '')))
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [])

  const carregarContas = useCallback(async () => {
    if (!selecionada) return setContas([])
    try {
      const [lista, ultimas] = await Promise.all([listarContas(selecionada), ultimasColetas(selecionada)])
      setContas(lista)
      setColetas(ultimas)
    } catch (e) {
      setErro((e as Error).message)
    }
  }, [selecionada])

  useEffect(() => {
    carregarMarcas()
  }, [carregarMarcas])

  useEffect(() => {
    setContas(null)
    carregarContas()
  }, [carregarContas])

  /** Executa, mostra o erro do banco se houver e recarrega marcas, contas e o filtro do topo. */
  const alterar = async (acao: () => Promise<unknown>) => {
    setSalvando(true)
    try {
      await acao()
      setErro(null)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await Promise.all([carregarMarcas(), carregarContas()])
      recarregarMarcas()
      setVersao((v) => v + 1)
      setSalvando(false)
    }
  }

  const confirmar = () => {
    const d = dialogo
    if (!d) return
    setDialogo(null)
    if (d.tipo === 'nova-marca') {
      alterar(async () => setSelecionada(await criarMarca(d.nome)))
    } else if (d.tipo === 'renomear-marca' && marca) {
      alterar(() => atualizarMarca(marca.id, { nome: d.nome.trim() }))
    } else if (d.tipo === 'ativa-marca' && marca) {
      alterar(() => atualizarMarca(marca.id, { ativa: d.ativa }))
    } else if (d.tipo === 'nova-conta' && marca) {
      setAviso(null)
      alterar(async () => {
        const id = await cadastrarConta(marca.id, d.plataforma, d.idExterno, d.nome)
        // Já faz a carga inicial: é o que confirma que o acesso está liberado.
        if (!COLETORES[d.plataforma]) {
          setAviso({ tipo: 'success', texto: `${d.nome.trim()} cadastrada.` })
          return
        }
        const falta = faltaPara(d.plataforma)
        if (falta) {
          setAviso({ tipo: 'warning', texto: `${d.nome.trim()} cadastrada, mas a primeira coleta não rodou: falta a credencial do MarketingHUB (${falta.join(', ')}) — avise o administrador.` })
          return
        }
        let r
        try {
          r = await coletarAgora(d.plataforma, id, DIAS_MAXIMO_COLETA[d.plataforma] ?? 90)
        } catch (e) {
          // A conta ficou cadastrada: o erro é só da coleta (não deixa parecer que nada foi salvo).
          setAviso({ tipo: 'error', texto: `${d.nome.trim()} cadastrada, mas a primeira coleta falhou: ${(e as Error).message}` })
          return
        }
        setAviso(
          r.status === 'ok'
            ? { tipo: r.mensagem ? 'warning' : 'success', texto: `${d.nome.trim()} cadastrada e coletada: ${r.linhas} linha(s)${r.mensagem ? `, com avisos — ${r.mensagem}` : '.'}` }
            : { tipo: 'error', texto: `${d.nome.trim()} cadastrada, mas a primeira coleta falhou: ${r.mensagem}` },
        )
      })
    } else if (d.tipo === 'renomear-conta') {
      alterar(() => atualizarConta(d.conta.id, { nome: d.nome.trim() }))
    } else if (d.tipo === 'excluir-marca' && marca) {
      alterar(async () => {
        const linhas = await excluirMarca(marca.id, d.confirmacao)
        const total = Object.values(linhas).reduce((s, n) => s + n, 0)
        setSelecionada('')
        setAviso({ tipo: 'success', texto: `${marca.nome} foi excluída de vez: ${total.toLocaleString('pt-BR')} linha(s) apagadas.` })
      })
    } else if (d.tipo === 'remover-demo' && marca) {
      alterar(async () => {
        const n = await removerDemonstracao(marca.slug)
        setAviso({ tipo: 'success', texto: `${marca.nome}: ${n} conta(s) de demonstração removidas com todos os dados fictícios.` })
      })
    } else if (d.tipo === 'coletar') {
      setAviso(null)
      alterar(async () => {
        const r = await coletarAgora(d.conta.plataforma, d.conta.id, d.dias)
        setAviso(
          r.status === 'ok'
            ? r.mensagem
              ? { tipo: 'warning', texto: `${d.conta.nome}: coleta concluída, ${r.linhas} linha(s) gravadas, com avisos — ${r.mensagem}` }
              : { tipo: 'success', texto: `${d.conta.nome}: coleta concluída, ${r.linhas} linha(s) gravadas.` }
            : { tipo: 'error', texto: `${d.conta.nome}: ${r.mensagem}` },
        )
      })
    }
  }

  /** Primeira coleta de uma conta traz o máximo do coletor (carga inicial); depois, 7 bastam. */
  const diasSugeridos = (c: ContaAdmin) => (coletas.get(c.id)?.status === 'ok' ? 7 : (DIAS_MAXIMO_COLETA[c.plataforma] ?? 90))

  const formato = dialogo?.tipo === 'nova-conta' ? FORMATO_ID[dialogo.plataforma] : undefined
  const idNormalizado = dialogo?.tipo === 'nova-conta' && formato ? formato.normalizar(dialogo.idExterno) : null

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
          Marcas
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          Cada marca e as contas dela em cada plataforma. É daqui que os coletores sabem o que buscar. Remover é
          desativar: a marca some dos filtros e de "Todas as marcas", mas o histórico fica guardado.
        </Typography>
      </Box>

      {gerencia ? <ColetaDiariaCard versao={versao} /> : null}
      {credenciais ? <CredenciaisCard s={credenciais} gerencia={gerencia} /> : null}

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert severity={aviso.tipo} onClose={() => setAviso(null)}>
          {aviso.texto}
        </Alert>
      ) : null}

      {!marcas ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)' },
            alignItems: 'start',
            opacity: salvando ? 0.6 : 1,
            transition: 'opacity 160ms ease',
          }}
        >
          <Paper variant="outlined">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
                Marcas
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setDialogo({ tipo: 'nova-marca', nome: '' })} sx={{ textTransform: 'none' }}>
                Nova marca
              </Button>
            </Stack>
            <List dense disablePadding>
              {marcas.map((m) => (
                <ListItemButton key={m.id} selected={m.id === selecionada} onClick={() => setSelecionada(m.id)}>
                  <ListItemText primary={m.nome} secondary={m.slug} sx={{ opacity: m.ativa ? 1 : 0.6 }} />
                  {!m.ativa ? <Chip size="small" label="Desativada" variant="outlined" /> : null}
                </ListItemButton>
              ))}
            </List>
          </Paper>

          {marca ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
                  <Box sx={{ mr: 'auto' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
                      {marca.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Identificador: {marca.slug} · {marca.ativa ? 'ativa' : 'desativada'}
                    </Typography>
                  </Box>
                  {gerencia ? (
                    <>
                      <Button size="small" onClick={() => setDialogo({ tipo: 'renomear-marca', nome: marca.nome })} sx={{ textTransform: 'none' }}>
                        Renomear
                      </Button>
                      <Button
                        size="small"
                        color={marca.ativa ? 'error' : 'primary'}
                        variant={marca.ativa ? 'text' : 'outlined'}
                        onClick={() => setDialogo({ tipo: 'ativa-marca', ativa: !marca.ativa })}
                        sx={{ textTransform: 'none' }}
                      >
                        {marca.ativa ? 'Desativar' : 'Reativar'}
                      </Button>
                    </>
                  ) : null}
                  {admin && !marca.ativa ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        setDialogo({ tipo: 'excluir-marca', confirmacao: '', contagem: null })
                        contarDadosMarca(marca.id)
                          .then((contagem) => setDialogo((d) => (d?.tipo === 'excluir-marca' ? { ...d, contagem } : d)))
                          .catch((e: Error) => setErro(e.message))
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Excluir
                    </Button>
                  ) : null}
                </Stack>
              </Paper>

              {marca.ativa && contas ? (
                <AtivacaoMarca
                  marca={marca.nome}
                  contas={contas}
                  coletas={coletas}
                  info={infoAtivacao}
                  onConectar={(plataforma) => setDialogo({ tipo: 'nova-conta', plataforma, idExterno: '', nome: '' })}
                />
              ) : null}

              <Paper variant="outlined">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
                    Contas conectadas
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {gerencia && contas?.some((c) => c.demo) ? (
                      <Button size="small" color="warning" onClick={() => setDialogo({ tipo: 'remover-demo' })} sx={{ textTransform: 'none' }}>
                        Remover demonstração
                      </Button>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setDialogo({ tipo: 'nova-conta', plataforma: 'meta_ads', idExterno: '', nome: '' })}
                      sx={{ textTransform: 'none' }}
                    >
                      Cadastrar conta
                    </Button>
                  </Stack>
                </Stack>
                {!contas ? (
                  <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : contas.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                    Nenhuma conta cadastrada. Cadastre as contas de anúncio, GA4, Search Console e redes da marca para
                    os coletores começarem a buscar os dados.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Plataforma</TableCell>
                          <TableCell>Nome</TableCell>
                          <TableCell>ID</TableCell>
                          <TableCell>Situação</TableCell>
                          <TableCell>Última coleta</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contas.map((c) => (
                          <TableRow key={c.id} sx={{ opacity: c.ativa ? 1 : 0.6 }}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{nomePlataforma(c.plataforma)}</TableCell>
                            <TableCell>{c.nome}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{c.idExterno}</TableCell>
                            <TableCell>
                              {c.demo ? (
                                <Chip size="small" label="Demonstração" color="warning" variant="outlined" />
                              ) : (
                                <Chip size="small" label={c.ativa ? 'Ativa' : 'Desativada'} variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              <UltimaColeta conta={c} coleta={coletas.get(c.id)} />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {/* Conta de demonstração não se mexe pelo app (o banco barra). */}
                              {!c.demo ? (
                                <>
                                  {COLETORES[c.plataforma] && c.ativa ? (
                                    faltaPara(c.plataforma) ? (
                                      <Tooltip title={`Falta a credencial: ${faltaPara(c.plataforma)!.join(', ')}`}>
                                        <span>
                                          <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none', mr: 0.5 }}>
                                            Falta credencial
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setDialogo({ tipo: 'coletar', conta: c, dias: diasSugeridos(c) })}
                                        sx={{ textTransform: 'none', mr: 0.5 }}
                                      >
                                        Coletar agora
                                      </Button>
                                    )
                                  ) : null}
                                  <Button size="small" onClick={() => setDialogo({ tipo: 'renomear-conta', conta: c, nome: c.nome })} sx={{ textTransform: 'none' }}>
                                    Renomear
                                  </Button>
                                  <Button
                                    size="small"
                                    color={c.ativa ? 'error' : 'primary'}
                                    onClick={() => alterar(() => atualizarConta(c.id, { ativa: !c.ativa }))}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {c.ativa ? 'Desativar' : 'Reativar'}
                                  </Button>
                                </>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Stack>
          ) : null}
        </Box>
      )}

      <Dialog open={Boolean(dialogo)} onClose={() => setDialogo(null)} fullWidth maxWidth="sm">
        {dialogo?.tipo === 'remover-demo' ? (
          <>
            <DialogTitle>Remover a demonstração de “{marca?.nome}”?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                Apaga as contas de demonstração desta marca e todos os dados fictícios delas, em todos os painéis. As
                outras marcas continuam com a demonstração. Faça isso quando a marca começar a receber dado real — senão o
                painel soma o real com o fictício. Não dá para desfazer.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button variant="contained" color="warning" onClick={confirmar}>
                Remover
              </Button>
            </DialogActions>
          </>
        ) : dialogo?.tipo === 'coletar' ? (
          <>
            <DialogTitle>Coletar “{dialogo.conta.nome}”</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField
                  select
                  label="Período"
                  value={dialogo.dias}
                  onChange={(e) => setDialogo({ ...dialogo, dias: Number(e.target.value) })}
                >
                  <MenuItem value={7}>Últimos 7 dias (atualização)</MenuItem>
                  <MenuItem value={30}>Últimos 30 dias{(DIAS_MAXIMO_COLETA[dialogo.conta.plataforma] ?? 90) === 30 ? ' (carga inicial)' : ''}</MenuItem>
                  {(DIAS_MAXIMO_COLETA[dialogo.conta.plataforma] ?? 90) >= 90 ? <MenuItem value={90}>Últimos 90 dias (carga inicial)</MenuItem> : null}
                </TextField>
                <Typography variant="body2" color="text.secondary">
                  {dialogo.conta.plataforma === 'instagram' || dialogo.conta.plataforma === 'facebook'
                    ? 'As métricas de cada dia do período são atualizadas, e as publicações dos últimos 30 dias também. O número de seguidores é o de hoje (a rede não informa o de dias passados).'
                    : 'O período inteiro é regravado com o que a plataforma informar agora — ela revisa os dias recentes (conversões atribuídas depois, dados ainda em processamento).'}{' '}
                  Pode levar até um minuto.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button variant="contained" onClick={confirmar}>
                Coletar
              </Button>
            </DialogActions>
          </>
        ) : dialogo?.tipo === 'excluir-marca' ? (
          <>
            <DialogTitle>Excluir “{marca?.nome}” de vez?</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                <Alert severity="error">
                  Não tem volta: a marca e todos os dados dela são apagados, inclusive o histórico e os leads. Se a ideia é só tirar a marca dos
                  painéis, deixe desativada.
                </Alert>
                {!dialogo.contagem ? (
                  <LinearProgress sx={{ borderRadius: 1 }} />
                ) : Object.keys(dialogo.contagem).length ? (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Vai ser apagado:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                      {Object.entries(ROTULOS_DADOS)
                        .filter(([t]) => dialogo.contagem![t])
                        .map(([t, rotulo]) => (
                          <Typography component="li" variant="body2" key={t}>
                            {dialogo.contagem![t].toLocaleString('pt-BR')} {rotulo}
                          </Typography>
                        ))}
                      {(() => {
                        const outros = Object.entries(dialogo.contagem!)
                          .filter(([t]) => !ROTULOS_DADOS[t])
                          .reduce((s, [, n]) => s + n, 0)
                        return outros ? (
                          <Typography component="li" variant="body2">
                            {outros.toLocaleString('pt-BR')} outros registros (conversões por dia, detalhe dos anúncios, coletas…)
                          </Typography>
                        ) : null
                      })()}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2">A marca não tem nenhum dado: só o cadastro sai.</Typography>
                )}
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label={`Digite “${marca?.nome}” para confirmar`}
                  value={dialogo.confirmacao}
                  onChange={(e) => setDialogo({ ...dialogo, confirmacao: e.target.value })}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button
                variant="contained"
                color="error"
                disabled={!dialogo.contagem || dialogo.confirmacao.trim().toLowerCase() !== (marca?.nome ?? '').trim().toLowerCase()}
                onClick={confirmar}
              >
                Excluir definitivamente
              </Button>
            </DialogActions>
          </>
        ) : dialogo?.tipo === 'ativa-marca' ? (
          <>
            <DialogTitle>{dialogo.ativa ? 'Reativar' : 'Desativar'} “{marca?.nome}”?</DialogTitle>
            <DialogContent>
              <Typography variant="body2">
                {dialogo.ativa
                  ? 'A marca volta aos filtros e a "Todas as marcas", com todo o histórico.'
                  : `A marca sai dos filtros e de "Todas as marcas" para todo mundo. Nenhum dado é apagado — dá para reativar depois.${admin ? ' Para excluir de vez, desative primeiro: o botão Excluir aparece na marca desativada.' : ''}`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button variant="contained" color={dialogo.ativa ? 'primary' : 'error'} onClick={confirmar}>
                {dialogo.ativa ? 'Reativar' : 'Desativar'}
              </Button>
            </DialogActions>
          </>
        ) : dialogo ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              confirmar()
            }}
          >
            <DialogTitle>
              {dialogo.tipo === 'nova-marca'
                ? 'Nova marca'
                : dialogo.tipo === 'nova-conta'
                  ? `Cadastrar conta em ${marca?.nome}`
                  : 'Renomear'}
            </DialogTitle>
            <DialogContent>
              {dialogo.tipo === 'nova-conta' ? (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    select
                    label="Plataforma"
                    value={dialogo.plataforma}
                    onChange={(e) => setDialogo({ ...dialogo, plataforma: e.target.value })}
                  >
                    {PLATAFORMAS_CADASTRO.map((p) => (
                      <MenuItem key={p} value={p}>
                        {nomePlataforma(p)}
                      </MenuItem>
                    ))}
                  </TextField>
                  {TEM_BUSCA.has(dialogo.plataforma) ? (
                    <EscolherConta
                      key={dialogo.plataforma}
                      plataforma={dialogo.plataforma}
                      cadastradas={new Set((contas ?? []).filter((c) => c.plataforma === dialogo.plataforma).map((c) => c.idExterno))}
                      info={infoAtivacao}
                      onEscolher={(id, nome) => setDialogo({ ...dialogo, idExterno: id, nome: dialogo.nome || `${nomePlataforma(dialogo.plataforma)} ${nome}` })}
                    />
                  ) : null}
                  <TextField
                    label="ID da conta"
                    placeholder={formato?.exemplo}
                    value={dialogo.idExterno}
                    onChange={(e) => setDialogo({ ...dialogo, idExterno: e.target.value })}
                    error={Boolean(dialogo.idExterno) && !idNormalizado}
                    helperText={
                      dialogo.idExterno && idNormalizado && idNormalizado !== dialogo.idExterno.trim()
                        ? `Vai ser salvo como ${idNormalizado}`
                        : formato?.ajuda
                    }
                  />
                  <TextField
                    label="Nome"
                    placeholder="Como aparece nas telas, ex.: Meta Ads Bellofestas"
                    value={dialogo.nome}
                    onChange={(e) => setDialogo({ ...dialogo, nome: e.target.value })}
                  />
                </Stack>
              ) : (
                <TextField
                  autoFocus
                  fullWidth
                  margin="dense"
                  label="Nome"
                  value={dialogo.nome}
                  onChange={(e) => setDialogo({ ...dialogo, nome: e.target.value })}
                  helperText={
                    dialogo.tipo === 'nova-marca' && dialogo.nome.trim()
                      ? `Identificador: ${slugDe(dialogo.nome)} (não muda depois)`
                      : undefined
                  }
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogo(null)}>Cancelar</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  !dialogo.nome.trim() ||
                  (dialogo.tipo === 'nova-marca' && !slugDe(dialogo.nome)) ||
                  (dialogo.tipo === 'nova-conta' && !idNormalizado)
                }
              >
                {dialogo.tipo === 'nova-marca' || dialogo.tipo === 'nova-conta' ? 'Cadastrar' : 'Salvar'}
              </Button>
            </DialogActions>
          </Box>
        ) : null}
      </Dialog>
    </Stack>
  )
}

function UltimaColeta({ conta, coleta }: { conta: ContaAdmin; coleta: Coleta | undefined }) {
  if (conta.demo) return <Typography variant="caption" color="text.secondary">—</Typography>
  if (!COLETORES[conta.plataforma]) {
    return (
      <Typography variant="caption" color="text.secondary">
        Coletor ainda não disponível
      </Typography>
    )
  }
  if (!coleta) {
    return (
      <Typography variant="caption" color="text.secondary">
        Nunca coletada
      </Typography>
    )
  }
  // "OK" com mensagem = coletou, mas a plataforma não informou alguma métrica (aviso).
  const comAviso = coleta.status === 'ok' && Boolean(coleta.mensagem)
  const cor = comAviso ? 'warning' : coleta.status === 'ok' ? 'success' : coleta.status === 'erro' ? 'error' : 'default'
  const rotulo = comAviso ? 'OK, com avisos' : coleta.status === 'ok' ? 'OK' : coleta.status === 'erro' ? 'Erro' : 'Rodando'
  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Chip size="small" label={rotulo} color={cor} variant="outlined" />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {quando.format(new Date(coleta.iniciadaEm))}
          {coleta.status === 'ok' && coleta.linhas != null ? ` · ${coleta.linhas} linha(s)` : ''}
        </Typography>
      </Stack>
      {coleta.mensagem ? (
        <Typography variant="caption" color={coleta.status === 'erro' ? 'error.main' : 'text.secondary'} sx={{ maxWidth: 280 }}>
          {coleta.mensagem}
        </Typography>
      ) : null}
    </Stack>
  )
}
